import { Component } from '@theme/component';

const PARAM = 'cause';

/**
 * Filters a server-rendered collection grid by cause and keeps the URL in sync.
 *
 * @typedef {object} CollectionListingRefs
 * @property {HTMLButtonElement[]} [chips] - Cause filter buttons; the first has an empty data-cause (All).
 * @property {HTMLLIElement[]} [items] - Listed collections; data-causes holds space-separated cause handles.
 * @property {HTMLElement} grid - The list that holds the items.
 * @property {HTMLElement} status - The single live region for result counts.
 * @property {HTMLElement} empty - Message shown when no items match.
 *
 * @extends {Component<CollectionListingRefs>}
 */
class CollectionListingComponent extends Component {
  requiredRefs = ['grid', 'status', 'empty'];

  /** @type {Set<string>} */
  #selected = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#sort();
    this.#writeCounts();
    this.#selected = this.#readUrl();
    this.#render({ announce: false, updateUrl: false });
    this.#revealActiveChip();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sort();
    this.#writeCounts();
    this.#render({ announce: false, updateUrl: false });
  }

  /**
   * Handles a chip press. The All chip clears the selection.
   * @param {Event} event
   */
  select(event) {
    const chip = event.target;
    if (!(chip instanceof HTMLButtonElement)) return;

    const cause = chip.dataset.cause ?? '';
    if (!cause) {
      this.#selected.clear();
    } else if (this.dataset.multi === 'true') {
      if (this.#selected.has(cause)) this.#selected.delete(cause);
      else this.#selected.add(cause);
    } else {
      const onlyThis = this.#selected.size === 1 && this.#selected.has(cause);
      this.#selected = onlyThis ? new Set() : new Set([cause]);
    }

    this.#render({ announce: true, updateUrl: true });
  }

  get #items() {
    return this.refs.items ?? [];
  }

  get #causeChips() {
    return (this.refs.chips ?? []).filter((chip) => chip.dataset.cause);
  }

  /** @param {HTMLElement} item */
  #causesOf(item) {
    return (item.dataset.causes ?? '').split(' ').filter(Boolean);
  }

  #sort() {
    if (this.dataset.sort !== 'count') return;
    const sorted = [...this.#items].sort(
      (a, b) => Number(b.dataset.count) - Number(a.dataset.count) || (a.dataset.title ?? '').localeCompare(b.dataset.title ?? '')
    );
    for (const item of sorted) this.refs.grid.append(item);
  }

  #writeCounts() {
    for (const chip of this.refs.chips ?? []) {
      const counter = chip.querySelector('.collection-listing__chip-count');
      if (!counter) continue;
      const cause = chip.dataset.cause;
      const total = cause ? this.#items.filter((item) => this.#causesOf(item).includes(cause)).length : this.#items.length;
      counter.textContent = String(total);
    }
  }

  /** @returns {Set<string>} */
  #readUrl() {
    const known = new Set(this.#causeChips.map((chip) => chip.dataset.cause ?? ''));
    const requested = (new URL(window.location.href).searchParams.get(PARAM) ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter((value) => known.has(value));
    return new Set(this.dataset.multi === 'true' ? requested : requested.slice(0, 1));
  }

  /** @param {{ announce: boolean, updateUrl: boolean }} options */
  #render({ announce, updateUrl }) {
    for (const chip of this.refs.chips ?? []) {
      const cause = chip.dataset.cause;
      const pressed = cause ? this.#selected.has(cause) : this.#selected.size === 0;
      chip.setAttribute('aria-pressed', String(pressed));
    }

    let shown = 0;
    for (const item of this.#items) {
      const match = this.#selected.size === 0 || this.#causesOf(item).some((cause) => this.#selected.has(cause));
      item.hidden = !match;
      if (match) shown += 1;
    }

    this.refs.empty.hidden = shown > 0;

    const labels = this.#causeChips
      .filter((chip) => this.#selected.has(chip.dataset.cause ?? ''))
      .map((chip) => chip.querySelector('.collection-listing__chip-label')?.textContent?.trim() ?? '');
    const plural = new Intl.PluralRules(document.documentElement.lang || undefined).select(shown) === 'one' ? 'One' : 'Other';
    const template = labels.length ? this.dataset[`filtered${plural}`] : this.dataset[`count${plural}`];
    const text = (template ?? '').replace('[count]', String(shown)).replace('[causes]', labels.join(', '));
    if (announce || this.refs.status.textContent !== text) this.refs.status.textContent = text;

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (this.#selected.size) url.searchParams.set(PARAM, [...this.#selected].join(','));
      else url.searchParams.delete(PARAM);
      window.history.replaceState(window.history.state, '', url);
    }
  }

  #revealActiveChip() {
    const active = this.#causeChips.find((chip) => this.#selected.has(chip.dataset.cause ?? ''));
    active?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
  }
}

if (!customElements.get('collection-listing-component')) {
  customElements.define('collection-listing-component', CollectionListingComponent);
}
